import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';

@Injectable({
    providedIn: 'root',
})
export class IndexDBService {
    private db: IDBDatabase;
    private dbName = 'micro_red';
    private tableName = 'portal_pagos';

    constructor(private messageService: MessageService) {
        this.openDatabase();
    }

    async openDatabase(): Promise<void> {
        try {
            if (this.db) {
                return; // La base de datos ya está abierta.
            }
            return new Promise<void>((resolve, reject) => {
                const request = indexedDB.open(this.dbName, 1);
                request.onerror = () => {
                    reject();
                };
                request.onsuccess = () => {
                    this.db = request.result;
                    resolve();
                };
                request.onupgradeneeded = (event: any) => {
                    this.db = event.target.result;
                    if (!this.db.objectStoreNames.contains(this.tableName)) {
                        const objectStore = this.db.createObjectStore(
                            this.tableName,
                            { keyPath: 'id', autoIncrement: true }
                        );
                        objectStore.createIndex('id_caja', 'id_caja', {
                            unique: false,
                        });
                    }
                };
            });
        } catch (error: any) {
            this.messageService.add({
                severity: 'error',
                summary:
                    'Error al iniciar crear la base de datos del navegador',
                detail: error,
            });
        }
    }

    // Método para agregar un registro
    async addRecord(record: any): Promise<any> {
        return new Promise<any>(async (resolve, reject) => {
            const transaction = this.db.transaction(
                [this.tableName],
                'readwrite'
            );
            const objectStore = transaction.objectStore(this.tableName);
            const request = objectStore.add(record);

            request.onerror = () => {
                reject('Error al agregar el registro');
            };

            request.onsuccess = (event: any) => {
                const key = event.target.result; // Obteniendo la clave generada
                const getRequest = objectStore.get(key);

                getRequest.onerror = () => {
                    reject('Error al obtener el registro recién agregado');
                };

                getRequest.onsuccess = () => {
                    resolve(getRequest.result); // Retornando el objeto creado
                };
            };
        });
    }

    //Metodo para obtener el ultimo registro agregado
    async getLastRecord(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            const transaction = this.db.transaction(
                [this.tableName],
                'readonly'
            );
            const objectStore = transaction.objectStore(this.tableName);
            const request = objectStore.openCursor(null, 'prev'); // 'prev' para empezar desde el último registro
            request.onerror = () => {
                // console.error('Error al obtener el registro más reciente');
                reject();
            };
            request.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest).result;
                if (cursor) {
                    // Esto nos dará el registro más reciente basado en el orden de inserción
                    // console.log('Registro más reciente obtenido correctamente');
                    resolve(cursor.value);
                } else {
                    // No hay registros
                    resolve(null);
                }
            };
        });
    }

    // Método para obtener todos los registros
    async getAllRecords(): Promise<any[]> {
        return new Promise<any[]>((resolve, reject) => {
            const transaction = this.db.transaction(
                [this.tableName],
                'readonly'
            );
            const objectStore = transaction.objectStore(this.tableName);

            const request = objectStore.getAll();

            request.onerror = () => {
                // console.error('Error al obtener todos los registros');
                reject();
            };
            request.onsuccess = (event) => {
                resolve((event.target as IDBRequest).result);
            };
        });
    }

    async getRegistro(cod_usuario: string): Promise<any[]> {
        return new Promise<any[]>((resolve, reject) => {
            if (!this.db) {
                // console.error('La base de datos no está abierta.');
                reject('La base de datos no está abierta.');
                return;
            }
            const transaction = this.db.transaction(
                [this.tableName],
                'readonly'
            );
            const objectStore = transaction.objectStore(this.tableName);
            const request = objectStore.get(cod_usuario);
            request.onerror = () => {
                // console.error('Error al obtener el registro');
                reject();
            };
            request.onsuccess = () => {
                // console.log('Registro obtenido correctamente');
                resolve(request.result);
            };
        });
    }

    async updateRecord(record: any): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const transaction = this.db.transaction(
                [this.tableName],
                'readwrite'
            );
            const objectStore = transaction.objectStore(this.tableName);
            const request = objectStore.put(record);
            request.onerror = () => {
                // console.error('Error al actualizar el registro');
                reject();
            };
            request.onsuccess = () => {
                // console.log('Registro actualizado correctamente');
                resolve();
            };
        });
    }

    // Método para eliminar un registro por su clave primaria
    async deleteRecord(codUsuario: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const transaction = this.db.transaction(
                [this.tableName],
                'readwrite'
            );
            const objectStore = transaction.objectStore(this.tableName);
            const request = objectStore.delete(codUsuario);
            request.onerror = () => {
                // console.error('Error al eliminar el registro');
                reject();
            };
            request.onsuccess = () => {
                // console.log('Registro eliminado correctamente');
                resolve();
            };
        });
    }

    async clearStore(storeName: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!this.db) {
                reject('La base de datos del navegador no está abierta');
                return;
            }
            if (!this.db.objectStoreNames.contains(storeName)) {
                reject(`La tabla "${storeName}" del navegador no existe`);
                return;
            }
            const transaction = this.db.transaction([storeName], 'readwrite');
            const objectStore = transaction.objectStore(storeName);
            const request = objectStore.clear();
            request.onsuccess = () => {
                resolve();
            };
            request.onerror = () => {
                reject(
                    `Error al limpiar la tabla "${storeName}" del navegador`
                );
            };
        });
    }
}
